import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import { clientsApi } from '../services/api';
import './DailyConsumptionChart.css';

interface MonthData {
  year: number;
  month: number;
  monthName: string;
  dailyTotals: { [key: number]: number };
  monthlyTotal: number;
  daysInMonth: number;
}

interface ChartDataPoint {
  day: number;
  date: string;
  [key: string]: any; // Para las líneas de cada mes
}

const DailyConsumptionChart: React.FC = () => {
  const [chartData, setChartData] = useState<MonthData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthsToShow, setMonthsToShow] = useState(3);
  const [processedData, setProcessedData] = useState<ChartDataPoint[]>([]);

  const fetchChartData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await clientsApi.getChartData(monthsToShow);
      if (response.success) {
        setChartData(response.data);
        processDataForChart(response.data);
      } else {
        setError(response.error || 'Error al cargar datos');
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const processDataForChart = (data: MonthData[]) => {
    // Encontrar el máximo número de días entre todos los meses
    const maxDays = Math.max(...data.map(month => month.daysInMonth));
    
    // Crear array de datos para la gráfica
    const chartPoints: ChartDataPoint[] = [];
    
    for (let day = 1; day <= maxDays; day++) {
      const point: ChartDataPoint = {
        day,
        date: `Día ${day}`
      };
      
      // Agregar datos de cada mes como una línea
      data.forEach(month => {
        const monthKey = `${month.monthName} ${month.year}`;
        point[monthKey] = day <= month.daysInMonth ? (month.dailyTotals[day] || 0) : null;
      });
      
      chartPoints.push(point);
    }
    
    setProcessedData(chartPoints);
  };

  useEffect(() => {
    fetchChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthsToShow]);

  const getLineColor = (index: number) => {
    const colors = [
      '#8884d8', // Azul
      '#82ca9d', // Verde
      '#ffc658', // Amarillo
      '#ff7c7c', // Rojo
      '#8dd1e1', // Celeste
      '#d084d0', // Morado
    ];
    return colors[index % colors.length];
  };

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              <span className="tooltip-name">{entry.dataKey}:</span>
              <span className="tooltip-value">
                {entry.value !== null ? `${entry.value.toLocaleString()} facturas` : 'Sin datos'}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <RefreshCw size={24} className="spinning" />
        <p>Cargando datos de consumo diario...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>❌ {error}</p>
        <button onClick={fetchChartData} className="retry-button">
          <RefreshCw size={16} />
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="daily-consumption-chart">
      <div className="chart-header">
        <div className="chart-title">
          <TrendingUp size={20} />
          <h3>Consumo Diario por Mes</h3>
        </div>
        
        <div className="chart-controls">
          <div className="months-selector">
            <Calendar size={16} />
            <label>Meses a mostrar:</label>
            <select 
              value={monthsToShow} 
              onChange={(e) => setMonthsToShow(parseInt(e.target.value))}
            >
              <option value={3}>3 meses</option>
              <option value={6}>6 meses</option>
              <option value={12}>12 meses</option>
            </select>
          </div>
          
          <button onClick={fetchChartData} className="refresh-button">
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>
      </div>

      <div className="chart-stats">
        {chartData.map((month, index) => (
          <div key={`${month.year}-${month.month}`} className="month-stat">
            <div className="stat-label">{month.monthName} {month.year}</div>
            <div className="stat-value" style={{ color: getLineColor(index) }}>
              {month.monthlyTotal.toLocaleString()} facturas
            </div>
          </div>
        ))}
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={processedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="date" 
              stroke="#666"
              fontSize={12}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#666"
              fontSize={12}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={customTooltip} />
            <Legend />
            
            {chartData.map((month, index) => (
              <Line
                key={`${month.year}-${month.month}`}
                type="monotone"
                dataKey={`${month.monthName} ${month.year}`}
                stroke={getLineColor(index)}
                strokeWidth={2}
                dot={{ fill: getLineColor(index), strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: getLineColor(index), strokeWidth: 2 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-info">
        <p>
          📊 Esta gráfica muestra el consumo diario de facturas a lo largo de diferentes meses.
          Cada línea representa un mes diferente, permitiendo comparar patrones de uso.
        </p>
      </div>
    </div>
  );
};

export default DailyConsumptionChart;
